import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios'
import { API_CONFIG } from '@/config/api'

/**
 * Standard API response format
 */
export interface ApiResponse<T = any> {
  success: boolean
  message?: string
  data?: T
  code?: string
}

/**
 * Create and configure Axios instance
 */
function createApiClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_CONFIG.baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Important for httpOnly cookies (JWT tokens)
    responseType: 'json',
  })

  return client
}

/**
 * Default API client instance
 */
const apiClient = createApiClient()

/**
 * Type-safe API request helper
 */
export async function apiRequest<T = any>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
  try {
    const response = await apiClient.request<ApiResponse<T>>(config)
    return response.data
  } catch (error: any) {
    // Extract error message from response
    if (error.response) {
      const data = error.response.data

      if (data && typeof data === 'object') {
        return data as ApiResponse<T>
      }
      // If data is a string, try to parse it
      if (data && typeof data === 'string') {
        try {
          const parsed = JSON.parse(data)
          return parsed as ApiResponse<T>
        } catch (e) {
          // Failed to parse, continue
        }
      }
    }
    return {
      success: false,
      message: error.message || 'An error occurred',
      code: 'NETWORK_ERROR',
    }
  }
}

export default apiClient

